﻿import { getCurrentInstance, inject } from 'vue'
import { VueLogger } from 'vue-logger-plugin'
import { Router, RouteLocationRaw } from 'vue-router'
import { useToast } from "primevue/usetoast";
import { ToastServiceMethods } from "primevue/toastservice";
import { ToastMessageOptions } from 'primevue/toast';
import { useConfirm } from "primevue/useconfirm";
import enums, { msg$, module$ } from '../enums'
import { useApp } from '../pinia'
import { i18n } from '@/locales';
import helper from './helper'
import { useMessenger } from '../pinia/messenger';
import { useSecurity } from '../pinia/security';
import { http } from './http'
import { assets } from './assets'

/** 訊息提示 */
class messenger {
    private util$: utility;
    //private vuex$: vuex;
    constructor(utility: utility) {
        this.util$ = utility;
    }

    /**
    *  送出彈出視窗需求
    *  @param {string} [type] - 類型: error/warn/info/success
    *  @param {string} [message] - 顯示訊息
    *  @param {object} [calback] - 對應事件處理onError/OnErrorClose...
    */
    message(type: string, message: string, calback: { [key: string]: any; } = {}): void {

        const {
            onError, onErrorClose,
            onWarn, onWarnClose,
            onInfo, onInfoClose,
            onSuccess, onSuccessClose } = calback ?? {};

        if (type == msg$.error)
            this.error(message, { onError, onErrorClose });
        else if (type == msg$.warn)
            this.warn(message, { onWarn, onWarnClose });
        else if (type == msg$.info)
            this.info(message, { onInfo, onInfoClose });
        else if (type == msg$.success)
            this.success(message, { onSuccess, onSuccessClose });
    }
    /**
    *  送出彈出錯誤視窗需求
    *  @param {string} [message] - 顯示訊息
    *  @param {object} [callback] - 對應事件處理onError/onErrorClose
    */
    error(message: string, callback: { [key: string]: any; } = {}): void {
        const { onError, onErrorClose } = callback ?? {};
        if (onError) {
            //避免執行onError例外未執行
            try {
                onError();
            }
            catch {
                //Ignored
            }
        }
        this.util$.pinia$.messenger.pushError({ msg: message, callback: { onErrorClose } });
    }
    /**
    *  送出彈出警告視窗需求
    *  @param {string} [message] - 警告訊息
    *  @param {object} [calback] - 對應事件處理onWarn/onWarnClose
    */
    warn(message: string, calback: { [key: string]: any; } = {}): void {
        const { onWarn, onWarnClose } = calback ?? {};
        if (onWarn) {
            //避免執行onWarn例外未執行
            try {
                onWarn();
            }
            catch {
                //Ignored
            }
        }
        this.util$.pinia$.messenger.pushWarn({ msg: message, callback: { onWarnClose } });
    }
    /**
    *  送出彈出訊息視窗需求
    *  @param {string} [message] - 一般訊息
    *  @param {object} [callback] - 對應事件處理onInfo/OnInfoClose
    */
    info(message: string, callback: { [key: string]: any; } = {}): void {
        const { onInfo, onInfoClose } = callback ?? {};
        if (onInfo) {
            //避免執行onInfo例外未執行
            try {
                onInfo();
            }
            catch {
                //Ignored
            }
        }
        this.util$.pinia$.messenger.pushInfo({ msg: message, callback: { onInfoClose } });
    }
    /**
    *  送出彈出成功視窗需求
    *  @param {string} [message] - 成功訊息
    *  @param {object} [callback] - 對應事件處理onSuccess/OnSuccessClose
    */
    success(message: string, callback: { [key: string]: any; } = {}): void {
        const { onSuccess, onSuccessClose } = callback ?? {};
        if (onSuccess) {
            //避免執行onSuccess例外未執行
            try {
                onSuccess();
            }
            catch {
                //Ignored
            }
        }
        this.util$.pinia$.messenger.pushSuccess({ msg: message, callback: { onSuccessClose } });
    }
    /**
     * 移除所有已顯示的訊息提示
     */
    clear(): void {
        this.util$.pinia$.messenger.clearMessages();
    }
}

/** 吐司提示 */
class toast {
    private toast$: ToastServiceMethods;

    constructor(toast: ToastServiceMethods) {
        this.toast$ = toast;
    }

    /**
     * 自訂新增 Toast 訊息（直接透傳至底層 toast$.add）
     * @param args 對應 ToastServiceMethods.add 的所有參數
     */
    add(...args: Parameters<ToastServiceMethods['add']>): ReturnType<ToastServiceMethods['add']> {
        return this.toast$.add(...args);
    }

    /**
     * 訊息提示
     * @param summary 概述
     * @param detail 詳述
     * @param group 顯示群組位置
     * @param life 訊息停留時間(msec)(<=0駐留)
     */
    info(summary: string, detail: string = '', group: string = "tr", life: number = 3000): void {
        const toast = { severity: 'info', 'summary': summary, 'detail': detail, 'group': group } as ToastMessageOptions;
        //sticky
        if (life > 0) {
            toast['life'] = life;
        }
        this.toast$.add(toast);
    }

    /**
     * 成功提示
     * @param summary 概述
     * @param detail 詳述
     * @param group 顯示群組位置
     * @param life 訊息停留時間(msec)(<=0駐留)
     */
    success(summary: string, detail: string = '', group: string = "tr", life: number = 3000): void {
        const toast = { severity: 'success', 'summary': summary, 'detail': detail, 'group': group } as ToastMessageOptions;
        //sticky
        if (life > 0) {
            toast['life'] = life;
        }
        this.toast$.add(toast)
    }

    /**
     * 警告提示
     * @param summary 概述
     * @param detail 詳述
     * @param group 顯示群組位置
     * @param life 訊息停留時間(msec)(<=0駐留)
     */
    warn(summary: string, detail: string = '', group: string = "tr", life: number = 6000): void {
        const toast = { severity: 'warn', 'summary': summary, 'detail': detail, 'group': group } as ToastMessageOptions;
        //sticky
        if (life > 0) {
            toast['life'] = life;
        }
        this.toast$.add(toast)
    }

    /**
     * 錯誤提示
     * @param summary 概述
     * @param detail 詳述
     * @param group 顯示群組位置
     * @param life 訊息停留時間(msec)
     */
    error(summary: string, detail: string = '', group: string = "tr", life: number = 6000): void {
        const toast = { severity: 'error', 'summary': summary, 'detail': detail, 'group': group } as ToastMessageOptions;
        //sticky
        if (life > 0) {
            toast['life'] = life;
        }
        this.toast$.add(toast)
    }
    /**
     * 關閉目前顯示的所有toast
     */
    closeAll(): void {
        this.toast$.removeAllGroups();
    }
}

/** 確認視窗 */
class confirm {
    private util$: utility;
    private confirm$: typeof useConfirm.prototype;

    constructor(util: utility, confirm: typeof useConfirm.prototype) {
        this.util$ = util;
        this.confirm$ = confirm;
    }
    /**
    /**
     * 打開彈出詢問視窗
     * @param event 點選彈出事件
     * @param header 詢問標題
     * @param question 詢問字串
     * @param accept [是]時呼叫函式
     * @param reject [否]時呼叫函式
     * @param group dialog/popup
     */
    open(event: Event | undefined, header: string, question: string, accept: Function, reject: Function, group: string | undefined = undefined): void {
        this.confirm$.require(
            {
                group: group,
                target: !event ? event : event.currentTarget,
                header: header,
                message: question,
                icon: 'fas fa-question',
                acceptLabel: i18n.global.t('common.yes'),
                rejectLabel: i18n.global.t('common.no'),
                acceptIcon: 'fas fa-check',
                rejectIcon: 'fas fa-times',
                rejectClass: 'p-button-secondary',
                accept: accept,
                reject: reject,
            });
    }
    /**
     * 關閉彈出視窗
     */
    close() {
        this.confirm$.close();
    }
}

/** 路由控制 */
class router {
    private util$: utility;

    constructor(utility: utility) {
        this.util$ = utility;
    }

    /**
     * 移往下一個網址
     * @param url 網址
     */
    push(to: RouteLocationRaw) {
        this.util$.log$.debug('[router$.push]', to);
        return this.util$.vueRouter$.push(to);
    }

    /**
     * 取代目前的網址
     * @param url 網址
     */
    replace(to: RouteLocationRaw) {
        this.util$.log$.debug('[router$.replace]', to);
        return this.util$.vueRouter$.replace(to);
    }

    /**
    * 返回上一個網址
    */
    back() {
        this.util$.log$.debug('[router$.back]');
        this.util$.vueRouter$.back();
    }

    /**
    * 前往下一個網址
    */
    forward() {
        this.util$.log$.debug('[router$.back]');
        this.util$.vueRouter$.forward();
    }

    /**
    * 使用名稱取得網址
    * @param name 網址名稱
    */
    getPath(name: string): string | undefined {
        const r = this.util$.vueRouter$.getRoutes().find(r => r.name == name);
        if (r) {
            return r.path;
        } else {
            this.util$.log$.warn(`[router$.getPath] cannot find name: ${name}`);
            return undefined;
        }
    }

    /**
    * 取得登入後首頁（路由 meta.isHome 標記者）
    * @returns RouteLocationRaw；找不到標記時回根路徑 '/'
    */
    resolveHome(): RouteLocationRaw {
        // 優先：後端 Bootstrap 依當前連線 port 回傳的 home（多 port 內外網場景，各 port 主頁不同）。
        // 單 port 或後端未設定時 home 為 null/undefined → 走下方 meta.isHome 預設，不影響常見單 port 場景。
        const backendHome = this.util$.pinia$.app.parameters?.home;
        if (backendHome) {
            return { path: backendHome };
        }
        // fallback：靜態 meta.isHome（後端未回 home 時的預設，向後相容）。
        const r = this.util$.vueRouter$.getRoutes().find(r => r.meta?.isHome);
        if (r && r.name) {
            return { name: r.name };
        } else {
            this.util$.log$.warn('[router$.resolveHome] 找不到 meta.isHome 路由，fallback 至 "/"');
            return { path: '/' };
        }
    }

    /**
    * 導向登入後首頁（取代當前歷史紀錄）
    */
    replaceHome() {
        return this.replace(this.resolveHome());
    }
}

/** 紀錄追蹤 */
class log {
    vueLogger$: VueLogger;
    logger$: string = '';
    enableInfo: string[] = ['debug', "info"];
    enableWarn: string[] = ["debug", "info", "warn"];
    enableError: string[] = ["debug", "info", "warn", "error"];

    /** 可設定的 log level（前端語彙；不含 plugin 的 'log' 恆顯級）。 */
    static readonly levels = ['debug', 'info', 'warn', 'error'] as const;

    /** 驗證並正規化 level：合法回該值，否則回 undefined（呼叫端據以忽略）。 */
    static normalizeLevel(lv: unknown): (typeof log.levels)[number] | undefined {
        return (typeof lv === 'string' && (log.levels as readonly string[]).includes(lv))
            ? lv as (typeof log.levels)[number]
            : undefined;
    }

    constructor(vuelogger: VueLogger, logger: string = '') {
        this.vueLogger$ = vuelogger;
        this.logger$ = logger;
    }

    /**
     * 動態設定 log level（即時生效、全域；非法值忽略並保留現值）。
     * 因所有元件共用同一個 vueLogger$，apply 後全 app 一起套用，無需 F5 或逐元件更新。
     * @param lv 'debug' | 'info' | 'warn' | 'error'
     * @returns 是否套用成功（false=非法值，已忽略）
     */
    setLevel(lv: unknown): boolean {
        const norm = log.normalizeLevel(lv);
        if (!norm) {
            this.warn(`[log.setLevel] 忽略非法 level：${String(lv)}（合法：${log.levels.join('/')}）`);
            return false;
        }
        this.vueLogger$.apply({ level: norm });
        return true;
    }

    /**
     * 當前 level 是否會放行 debug（高成本 log 的前置 gate）。
     * 註：vue-logger-plugin 的 .d.ts 將 enabled/level 誤宣告為 method，實作其實是 getter；
     *     故以 as any 取「屬性值」，避開 TS2774（否則 enabled 被當函式參考、條件恆 true）。
     */
    isDebug(): boolean {
        const lg = this.vueLogger$ as any;
        return !!lg.enabled && String(lg.level) === 'debug';
    }

    debug(...args: any[]): void {
        if (!this.vueLogger$.enabled) return;
        if (String(this.vueLogger$.level) != 'debug') return;

        const hhmmss = new Date().toTimeString().split(' ')[0];
        if (!this.logger$) {
            args.unshift(hhmmss);
            this.vueLogger$.debug(...args);
            return;
        }
        args.unshift(hhmmss, `[${this.logger$}]`);
        this.vueLogger$.debug(...args);
    }
    info(...args: any[]): void {
        if (!this.vueLogger$.enabled) return;
        if (this.enableInfo.indexOf(String(this.vueLogger$.level)) == -1) return;

        const hhmmss = new Date().toTimeString().split(' ')[0];
        if (!this.logger$) {
            args.unshift(hhmmss);
            this.vueLogger$.info(...args);
            return;
        }
        args.unshift(hhmmss, `[${this.logger$}]`);
        this.vueLogger$.info(...args);
    }
    warn(...args: any[]): void {
        if (!this.vueLogger$.enabled) return;
        if (this.enableWarn.indexOf(String(this.vueLogger$.level)) == -1) return;

        const hhmmss = new Date().toTimeString().split(' ')[0];
        if (!this.logger$) {
            args.unshift(hhmmss);
            this.vueLogger$.warn(...args);
            return;
        }
        args.unshift(hhmmss, `[${this.logger$}]`);
        this.vueLogger$.warn(...args);
    }
    error(...args: any[]): void {
        if (!this.vueLogger$.enabled) return;
        if (this.enableError.indexOf(String(this.vueLogger$.level)) == -1) return;

        const hhmmss = new Date().toTimeString().split(' ')[0];
        if (!this.logger$) {
            args.unshift(hhmmss);
            this.vueLogger$.error(...args);
            return;
        }
        args.unshift(hhmmss, `[${this.logger$}]`);
        this.vueLogger$.error(...args);
    }
}

/** pinia狀態管理 */
class pinia {
    static util$: utility;
    
    constructor(util: utility) {
        pinia.util$ = util;
    }

    /**
     * main
     **/
    get app() {
        return useApp();
    }

    /**
     * messenger
     **/
    get messenger() {
        return useMessenger();
    }

    /**
     * security
     **/
    get security() {
        return useSecurity();
    }
}


export const signalrEvent$ = {
    //確保初次連線後任何時間點訂閱都能立即得到通知
    ready: '__ready__',
    connected: '__connected__',
    disconnected: '__disconnected__',
    reconnecting: '__reconnecting__',
    reconnected: '__reconnected__'
} as const;
// 提取系統事件的值型別
export type SignalrSystemEvent = typeof signalrEvent$[keyof typeof signalrEvent$];
// 基礎回呼函式型別
export type signalrCallback = (...args: any[]) => void;
// 各項功能的函式定義
export type signalrOn = (methodName: SignalrSystemEvent | string, callback: signalrCallback) => void;
export type signalrOff = (methodName: SignalrSystemEvent | string, callback?: signalrCallback) => void;
export type signalrSend = (methodName: string, ...args: any[]) => Promise<void>;
export type signalrInquire = <T = any>(handler: string, func: string, message: any) => Promise<T>;
export type signalrNotify = (handler: string, func: string, message: any) => Promise<void>;
export type signalrGroupOp = (handler: string, func: string, group: string) => Promise<void>;
class signalR
{
    // 系統事件 Event Bus (Map)
    private static listeners: Map<string, Array<(...args: any[]) => void>> = new Map();
    // 暫存隊列：當連線尚未 Ready 時，先存下使用者想註冊的業務事件
    private static pendingRegistry: Array<{ methodName: string, callback: any }> = [];
    // 橋接函式 (由 SignalR.vue 注入)
    private static $on: signalrOn | null = null;
    private static $off: signalrOff | null = null;
    private static $send: signalrSend | null = null;
    private static $inquire: signalrInquire | null = null;
    private static $notify: signalrNotify | null = null;
    private static $addToGroup: signalrGroupOp | null = null;
    private static $removeFromGroup: signalrGroupOp | null = null;
    //追蹤 Ready 狀態
    private static isReady: boolean = false;

    // --- 注入器 (Internal Setters) ---
    static ___setBridge___(methods: {
        on: signalrOn,
        off: signalrOff,
        send: signalrSend,
        inquire: signalrInquire,
        notify: signalrNotify,
        addToGroup: signalrGroupOp,
        removeFromGroup: signalrGroupOp
    }) {
        this.$on = methods.on;
        this.$off = methods.off;
        this.$send = methods.send;
        this.$inquire = methods.inquire;
        this.$notify = methods.notify;
        this.$addToGroup = methods.addToGroup;
        this.$removeFromGroup = methods.removeFromGroup;

        // 當橋接完成，立即清空暫存隊列，將業務事件補註冊到實體 Client 上
        // 直接呼叫 $on (實體註冊)，避免再次經過公用 on() 的分流檢查
        while (this.pendingRegistry.length > 0) {
            const item = this.pendingRegistry.shift();
            if (item && this.$on) {
                this.$on(item.methodName, item.callback);
            }
        }
    }

    /** 手動觸發系統事件 (僅用於內部或 signalrEvent$) */
    static emit(methodName: string, ...args: any[]) {
        // --- 當 emit ready 時，記錄狀態 ---
        if (methodName === signalrEvent$.ready) {
            this.isReady = true;
        }
        if (methodName === signalrEvent$.disconnected) {
            this.isReady = false; // 斷線時重置狀態
        }
        const callbacks = this.listeners.get(methodName);
        if (callbacks) {
            callbacks.forEach(cb => cb(...args));
        }
    }

    /** 註冊監聽：分流處理系統與業務事件 */
    static on(methodName: SignalrSystemEvent | string, callback: signalrCallback) { 
        const isSystemEvent = Object.values(signalrEvent$).includes(methodName as any);
        if (isSystemEvent) {
            // 處理 Ready 的補償機制 ---
            if (methodName === signalrEvent$.ready && this.isReady) {
                // 如果已經 Ready 了，直接執行回呼，不要等事件廣播
                callback();
                return;
            }
            if (!this.listeners.has(methodName)) this.listeners.set(methodName, []);
            this.listeners.get(methodName)?.push(callback);
        } else {
            if (this.$on) {
                this.$on(methodName, callback);
            } else {
                this.pendingRegistry.push({ methodName, callback });
            }
        }
    }

    /** 移除監聽 */
    static off(methodName: SignalrSystemEvent | string, callback?: signalrCallback) {
        const isSystemEvent = Object.values(signalrEvent$).includes(methodName as any);
        if (isSystemEvent) {
            const arr = this.listeners.get(methodName);
            if (callback) {
                const idx = arr?.indexOf(callback) ?? -1;
                if (idx !== -1) arr?.splice(idx, 1);
            } else {
                this.listeners.delete(methodName);
            }
        } else {
            // 這邊使用了 ?. 確保 $off 存在才執行
            this.$off?.(methodName, callback);
        }
    }
    
    // --- 封裝通訊方法 ---
    /** 傳送業務事件 (Fire-and-forget) */
    static async send(methodName: string, ...args: any[]): Promise<void> {
        if (!this.$send) throw new Error("SignalR Bridge [send] not initialized. Ensure connection is started.");
        return await this.$send(methodName, ...args);
    }

    /** 發送請求並等待結果 (RPC 模式) */
    static async inquire<T = any>(handler: string, func: string, message: any): Promise<T> {
        if (!this.$inquire) throw new Error("SignalR Bridge [inquire] not initialized. Ensure connection is started.");
        return await this.$inquire<T>(handler, func, message);
    }

    /** 發送通知訊息 (通用轉發模式) */
    static async notify(handler: string, func: string, message: any): Promise<void> {
        if (!this.$notify) throw new Error("SignalR Bridge [notify] not initialized. Ensure connection is started.");
        return await this.$notify(handler, func, message);
    }

    /** 加入群組 */
	static async addToGroup(handler: string, func: string, group: string): Promise<void> {
        if (!this.$addToGroup) throw new Error("SignalR Bridge [addToGroup] not initialized.");
		await this.$addToGroup(handler, func, group);
		utilfcty$.util$.log$.info(`[SignalR] Added to group: ${group} (Handler: ${handler}, Function: ${func})`);
    }

    /** 離開群組 */
	static async removeFromGroup(handler: string, func: string, group: string): Promise<void> {
        if (!this.$removeFromGroup) throw new Error("SignalR Bridge [removeFromGroup] not initialized.");
		await this.$removeFromGroup(handler, func, group);
		utilfcty$.util$.log$.info(`[SignalR] Removed from group: ${group} (Handler: ${handler}, Function: ${func})`);
    }

    // ...
    /** * 清除所有系統與業務事件監聽 (通常用於登出或徹底重置) 
     */
    static clearAllListeners() {
        this.listeners.clear();
        this.pendingRegistry = [];
        utilfcty$.util$.log$.info('All SignalR listeners have been cleared.');
    }
}

export class utility {
    private $vueRouter: Router;
    private $router: router;
    private $vueLogger: VueLogger;
    private $log: log;
    private $toast: toast | undefined;
    private $confirm: confirm | undefined;
    private $messenger: messenger;
    private $pinia: pinia;
    private $helper: helper;
    private $http: http;
    public static signalR$ = signalR;

    constructor(vueRouter: Router, vueLogger: VueLogger, toast: toast | undefined = undefined, confirm: confirm | undefined = undefined, logger: string = '') {
        this.$vueRouter = vueRouter;
        this.$router = new router(this);
        this.$vueLogger = vueLogger;
        this.$log = new log(vueLogger, logger);
        this.$toast = toast;
        this.$confirm = confirm;
        this.$messenger = new messenger(this);
        this.$pinia = new pinia(this);
        this.$helper = new helper();
        this.$http = new http(this);
    }

    /**
     * 指定吐司提示物件
     * @param val ToastServiceMethods
     */
    setToast(val: ToastServiceMethods) {
        this.$toast = new toast(val);
    }

    /**
     * 指定確認視窗物件
     */
    setConfirm(val: typeof useConfirm.prototype) {
        this.$confirm = new confirm(this, val);
    }

    /**
     * vuex狀態操作物件
     * @returns {router}
     */
    get router$(): router {
        return this.$router;
    }

    /**
     * 日誌操作物件
     * @returns {log}
     */
    get log$(): log {
        return this.$log;
    }

    /**
     * 訊息提示
     * @returns {messenger}
     */
    get messenger$(): messenger {
        return this.$messenger;
    }

    /**
     * vue router操作物件
     * @returns {Router}
     */
    get vueRouter$(): Router {
        return this.$vueRouter;
    }

    /**
     * pinia狀態管理
     **/
    get pinia$(): pinia {
        return this.$pinia;
    }

    /**
     * 吐司提示
     * @returns {toast}
     */
    get toast$(): toast | undefined {
        return this.$toast;
    }

    /**
     * 確認視窗
     */
    get confirm$(): confirm | undefined {
        return this.$confirm;
    }

    /**
     * helper
     * @returns {helper}
     */
    get helper$(): helper {
        return this.$helper;
    }

    /**
     * HTTP 請求物件
     */
    get http$(): http {
        return this.$http;
    }

    /**
     * assets
     */
    get assets$()  {
        return assets;
    }
    
    /**
     * 各種列舉值
     */
    get enums$(): typeof enums {
        return enums;
    }

    /**
     * Vue檔內取得util$
     * @param logger 紀錄log時的執行位置字串
     */
    vueUse(logger: string = '') {
        const util: utility =
            new utility(this.$vueRouter, this.$vueLogger, this.$toast, this.$confirm, logger);
        if (logger) {
            util.log$.debug('[util$.vueUse]');
        }
        return util;
    }
   
    /**
    * 檢查是否為Error的錯誤
    * @param error
    */
    isError(error: object): error is Error {
        return (error as Error).message !== undefined;
    }

    /**
     * 
     * @param error
     * @param options
     * @returns
     */
    handleError = (error: any, options: {
        showMessage?: boolean,
        context?: string,
        onProcessed?: () => void
    } = {}) => {
        const { showMessage = true, context = '', onProcessed } = options;
        const { t } = i18n.global; // 使用全域 i18n 實體

        let message = '';
        let errorCode = 0;

        // 1. 解析錯誤類型
        if (error.response) {
            // Axios 伺服器回傳錯誤 (4xx, 5xx)
            errorCode = error.response.status;
            message = error.response.data?.message || t('common.error.server');
        } else if (error.request) {
            // Axios 請求發出但無回應 (網路斷線)
            message = t('common.error.network');
        } else {
            // 原生 JS 報錯 (如之前的 SyntaxError) 或自定義錯誤
            message = error.message || t('common.error.unknown');
        }

        // 2. 錯誤紀錄 (這對您維護 MPPNext 很重要)
        this.log$.error(`[Exception][${context}]`, error);

        // 3. 是否彈出 UI 通知
        // 注意：如果您的全域攔截器已經彈了 Toast，這裡可以設為 false 避免重複
        if (showMessage) {
            this.messenger$.error(message);
        }

        // 4. 執行回呼 (例如重置 Loading 狀態或清除密碼欄位)
        if (onProcessed) {
            onProcessed();
        }

        // 5. 根據特定狀態碼執行強制行為 (補足攔截器)
        if (errorCode === 401) {
            // 雖然攔截器會處理，但這裡可以做額外的 local 清理
        }

        // 最後選擇是否要將錯誤繼續拋出，讓呼叫端 (如 loadFor) 接收
        return Promise.reject(error);
    }
}

export class utilfcty$ {
    static util$: utility;
    /**
     * 在vue檔內取得util$
     * @param logger 紀錄log時的執行位置字串
     */
    static vueUse(logger: string = ''): utility {
        const util: utility = inject('utility') as utility;
        if (util && !util.toast$) {
            util.setToast(useToast());
            util.setConfirm(useConfirm());
            utilfcty$.util$ = util;
        }
        const inst = getCurrentInstance();
        if (logger) {
            return utilfcty$.util$.vueUse(logger);
        }
        else if (inst && inst.type && inst.type.name) {
            return utilfcty$.util$.vueUse(inst.type.name);
        }
        return utilfcty$.util$.vueUse();
    }
}

export default {
    install: (app: any, options: { [key: string]: any; }) => {
        const {router, logger } = options;
        app.provide('utility', new utility(router, logger));
    }
}